import React, { useState } from "react";
import { useForm } from "react-hook-form";
import Icon from "../../../components/AppIcon"; // Assuming this component exists

const ContactForm = ({ services }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm();

  // Static options for Budget and Timeline
  const budgetRanges = [
    { value: "", label: "Select budget range" },
    { value: "under-10k", label: "Under $10,000" },
    { value: "10k-25k", label: "$10,000 - $25,000" },
    { value: "25k-50k", label: "$25,000 - $50,000" },
    { value: "50k-100k", label: "$50,000 - $100,000" },
    { value: "over-100k", label: "Over $100,000" },
    { value: "discuss", label: "Prefer to discuss" },
  ];

  const timelineOptions = [
    { value: "", label: "Select timeline" },
    { value: "asap", label: "ASAP" },
    { value: "1-month", label: "Within 1 month" },
    { value: "2-3-months", label: "2-3 months" },
    { value: "3-6-months", label: "3-6 months" },
    { value: "6-months-plus", label: "6+ months" },
    { value: "flexible", label: "Flexible" },
  ];

  const onFormSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      // Use environment variables for API endpoint
      const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_API_URL; // Handle both Vite and Create React App env variables

      if (!API_URL) {
        console.error("API URL is not defined in environment variables.");
        alert("API configuration error. Please contact support.");
        setIsSubmitting(false);
        return;
      }

      const response = await fetch(`${API_URL}/contactUs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // No API Key in Authorization header as per your second example, adjust if needed
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("Form submitted:", result);

      reset(); // Reset the form fields
      alert(result?.ResponseMessage || "Thank you! We’ll get back to you shortly.");
    } catch (err) {
      console.error("Form submission error:", err);
      alert(`Something went wrong. Please try again. ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const messageLength = watch("message")?.length || 0;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-surface rounded-lg card-shadow-lg p-8">
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Full Name *
              </label>
              <input
                type="text"
                {...register("name", {
                  required: "Full name is required",
                  minLength: { value: 2, message: "Name must be at least 2 chars" },
                })}
                className={`w-full px-4 py-3 border border-border rounded-md focus-ring ${errors.name ? "border-error" : ""
                  }`}
                placeholder="Your Name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-error flex items-center">
                  <Icon name="AlertCircle" size={16} className="mr-1" />
                  {errors.name.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Email Address *
              </label>
              <input
                type="email"
                {...register("email", {
                  required: "Email address is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Please enter a valid email address",
                  },
                })}
                className={`w-full px-4 py-3 border border-border rounded-md focus-ring ${errors.email ? "border-error" : ""
                  }`}
                placeholder="zentirx@infotech.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-error flex items-center">
                  <Icon name="AlertCircle" size={16} className="mr-1" />
                  {errors.email.message}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Company
              </label>
              <input
                type="text"
                {...register("company_name")} // Changed to company_name to match the second example's potential backend expectation
                className="w-full px-4 py-3 border border-border rounded-md focus-ring"
                placeholder="Your Company"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                {...register("phone", {
                  required: "Phone number is required",
                  pattern: {
                    value: /^[+0-9() ]{7,20}$/,
                    message: "Please enter a valid phone number",
                  },
                })}
                onInput={(e) => {
                  // Allow only digits, +, (, ), space
                  e.target.value = e.target.value.replace(/[^0-9+() ]/g, "");
                }}
                className={`w-full px-4 py-3 border border-border rounded-md focus-ring ${errors.phone ? "border-error" : ""
                  }`}
                placeholder="(+91) 9874566321"
              />

              {errors.phone && (
                <p className="mt-1 text-sm text-error flex items-center">
                  <Icon name="AlertCircle" size={16} className="mr-1" />
                  {errors.phone.message}
                </p>
              )}
            </div>
          </div>

          {/* Project Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Service of Interest
              </label>
              <select
                {...register("service", { required: "Please select a service" })}
                className={`w-full px-4 py-3 border border-border rounded-md focus-ring ${errors.service ? "border-error" : ""
                  }`}
              >
                <option value="">Select a service</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.title}
                  </option>
                ))}
                <option value="multiple">Multiple Services</option>
                <option value="consultation">General Consultation</option>
              </select>
              {errors.service && (
                <p className="mt-1 text-sm text-error flex items-center">
                  <Icon name="AlertCircle" size={16} className="mr-1" />
                  {errors.service.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Budget Range
              </label>
              <select
                {...register("budget")}
                className="w-full px-4 py-3 border border-border rounded-md focus-ring"
              >
                {budgetRanges.map((range) => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Timeline
              </label>
              <select
                {...register("timeline")}
                className="w-full px-4 py-3 border border-border rounded-md focus-ring"
              >
                {timelineOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Project Description */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Project Description *
            </label>
            <textarea
              rows={6}
              {...register("message", {
                required: "Please provide project details",
                minLength: {
                  value: 10,
                  message: "Please provide at least 10 characters",
                },
                maxLength: {
                  value: 1000,
                  message: "Message cannot exceed 1000 characters",
                },
              })}
              className={`w-full px-4 py-3 border border-border rounded-md focus-ring resize-vertical ${errors.message ? "border-error" : ""
                }`}
              placeholder="Please describe your project requirements, goals, and any specific needs or challenges you're facing..."
            ></textarea>
            <div className="flex justify-between items-center mt-2">
              {errors.message && (
                <p className="text-sm text-error flex items-center">
                  <Icon name="AlertCircle" size={16} className="mr-1" />
                  {errors.message.message}
                </p>
              )}
              <p
                className={`text-sm ${messageLength > 900 ? "text-warning" : "text-text-secondary"
                  }`}
              >
                {messageLength}/1000
              </p>
            </div>
          </div>

          {/* Newsletter Opt-in */}
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              {...register("newsletter")}
              className="mt-1 h-4 w-4 border-border rounded"
            />
            <label className="text-sm text-text-secondary">
              I would like to receive updates via email.
            </label>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`btn-primary px-8 py-3 text-lg flex items-center space-x-2 ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Icon name="Send" size={20} />
                  <span>Send Inquiry</span>
                </>
              )}
            </button>
          </div>

          {/* Contact Information (Optional, can be kept or removed) */}
          <div className="border-t border-border pt-6 mt-8">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                Prefer to talk directly?
              </h3>
              <p className="text-text-secondary">
                We're here to help you find the perfect solution for your business
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="flex flex-col items-center space-y-2">
                <div className="w-12 h-12 bg-primary-50 rounded-full flex items-center justify-center">
                  <Icon name="Phone" size={20} color="var(--color-primary)" />
                </div>
                <div>
                  <div className="font-medium text-text-primary">Call Us</div>
                  <div className="text-text-secondary">+91 9925627972</div>
                </div>
              </div>

              <div className="flex flex-col items-center space-y-2">
                <div className="w-12 h-12 bg-primary-50 rounded-full flex items-center justify-center">
                  <Icon name="Mail" size={20} color="var(--color-primary)" />
                </div>
                <div>
                  <div className="font-medium text-text-primary">Email Us</div>
                  <div className="text-text-secondary">info@zentirx.com</div>
                </div>
              </div>

              <div className="flex flex-col items-center space-y-2">
                <div className="w-12 h-12 bg-primary-50 rounded-full flex items-center justify-center">
                  <Icon name="MessageCircle" size={20} color="var(--color-primary)" />
                </div>
                <div>
                  <div className="font-medium text-text-primary">Live Chat</div>
                  <div className="text-text-secondary">Available 9AM-7PM IST</div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;